import 'dayjs/locale/ko';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// ----------------------------------------------------------------------

/**
 * @Docs
 * https://day.js.org/docs/en/display/format
 */

/**
 * Default timezones
 * https://day.js.org/docs/en/timezone/set-default-timezone#docsNav
 *
 */

/**
 * UTC
 * https://day.js.org/docs/en/plugin/utc
 * @install
 * import utc from 'dayjs/plugin/utc';
 * dayjs.extend(utc);
 * @usage
 * dayjs().utc().format()
 *
 */

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.locale('ko');

// ----------------------------------------------------------------------

// export const formatPatterns = {
//   dateTime: 'DD MMM YYYY h:mm a', // 17 Apr 2022 12:00 am
//   date: 'DD MMM YYYY', // 17 Apr 2022
//   time: 'h:mm a', // 12:00 am
//   split: {
//     dateTime: 'DD/MM/YYYY h:mm a', // 17/04/2022 12:00 am
//     date: 'DD/MM/YYYY', // 17/04/2022
//   },
//   paramCase: {
//     dateTime: 'DD-MM-YYYY h:mm a', // 17-04-2022 12:00 am
//     date: 'DD-MM-YYYY', // 17-04-2022
//   },
// };

export const formatPatterns = {
  dateTime: 'YYYY-MM-DD HH:mm:ss',
  date: 'YYYY-MM-DD', // 17 Apr 2022
  dateDot: 'YYYY.MM.DD',
  time: 'HH:mm:ss',
  split: {
    dateTime: 'YYYY-MM-DD HH:mm:ss',
    date: 'YYYY-MM-DD', // 17/04/2022
  },
  paramCase: {
    dateTime: 'YYYY-MM-DD HH:mm:ss',
    date: 'YYYY-MM-DD', // 17-04-2022
  },
  yyyymmdd: 'YYYYMMDD',
};

const isValidDate = (date: unknown) => date !== null && date !== undefined && dayjs(date as dayjs.ConfigType).isValid();

// ----------------------------------------------------------------------

export function today(template?: string) {
  return dayjs(new Date()).startOf('day').format(template);
}

// ----------------------------------------------------------------------

/**
 * @output 17 Apr 2022 12:00 am
 */

// ----------------------------------------------------------------------


// ----------------------------------------------------------------------

/**
 * @output 17 Apr 2022
 */

// ----------------------------------------------------------------------

// export function fDate(date, template) {
//   const realDate = date && typeof date.toDate === 'function' ? date.toDate() : date;
 
//   if (!isValidDate(date)) {
//     return 'Invalid date';
//   }

//   return dayjs(date).format(template ?? formatPatterns.date);
// }

// ⚠️ 두 번째 인자는 **원래부터 무시된다** — fDateRangeShortLabel 이 `fDate(d, 'DD MMM')` 로
// 부르지만 JS 시절부터 template 은 버려지고 formatPatterns.date 로만 찍혔다(선존 · A번호 후보).
// 동작 무변을 위해 받기만 하고 쓰지 않는다.
export function fDate(date: unknown, _ignoredTemplate?: string) {
  // Firestore Timestamp 객체라면 JS Date로 변환
  const asTs = date as { toDate?: () => Date } | null | undefined;
  const realDate = date && typeof asTs?.toDate === 'function' ? asTs.toDate() : date;
  if (!isValidDate(realDate)) {
    return 'Invalid date';
  }
  return dayjs(realDate as dayjs.ConfigType).format(formatPatterns.date);
}

// ----------------------------------------------------------------------

export function fDateDot(date: unknown) {
  // Firestore Timestamp 객체라면 JS Date로 변환
  const asTs = date as { toDate?: () => Date } | null | undefined;
  const realDate = date && typeof asTs?.toDate === 'function' ? asTs.toDate() : date;
  if (!isValidDate(realDate)) {
    return 'Invalid date';
  }
  return dayjs(realDate as dayjs.ConfigType).format(formatPatterns.dateDot);
}

// ----------------------------------------------------------------------

/**
 * @output 12:00 am
 */

// ----------------------------------------------------------------------

export function fTime(date: unknown, template?: string) {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date as dayjs.ConfigType).format(template ?? formatPatterns.time);
}

// ----------------------------------------------------------------------

/**
 * @output a few seconds, 2 years
 */

// ----------------------------------------------------------------------

export function fToNow(date: unknown) {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date as dayjs.ConfigType).toNow(true);
}

// ----------------------------------------------------------------------

/**
 * @output boolean
 */

// ----------------------------------------------------------------------


// ----------------------------------------------------------------------

/**
 * @output boolean
 */

// ----------------------------------------------------------------------

export function fIsAfter(startDate: unknown, endDate: unknown) {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  return dayjs(startDate as dayjs.ConfigType).isAfter(endDate as dayjs.ConfigType);
}

// ----------------------------------------------------------------------

/**
 * @output boolean
 */

// ----------------------------------------------------------------------

function fIsSame(startDate: unknown, endDate: unknown, unitToCompare?: dayjs.OpUnitType) {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  return dayjs(startDate as dayjs.ConfigType).isSame(endDate as dayjs.ConfigType, unitToCompare ?? 'year');
}

/**
 * @output
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 */

// ----------------------------------------------------------------------

export function fDateRangeShortLabel(startDate: unknown, endDate: unknown, initial?: boolean) {
  if (!isValidDate(startDate) || !isValidDate(endDate) || fIsAfter(startDate, endDate)) {
    return 'Invalid date';
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) {
    return label;
  }

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

// ----------------------------------------------------------------------

export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}: {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
}) {
  const result = dayjs()
    .subtract(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}


/**
 * 포스트의 createdAt 필드만을 기준으로 날짜 차이를 계산하는 유틸리티 함수
 * @param {Object} post - 포스트 객체
 * @param {Date} targetDate - 기준 날짜
 * @param {string} range - 계산 단위 ('hours', 'days', 'weeks')
 * @returns {number} 차이 값 (createdAt이 없으면 -1 반환)
 */
const indexOfDate = (rawPost: unknown, targetDate: Date | null | undefined, range = 'days') => {
  const post = rawPost as { createdAt?: { _seconds?: number } | string | number | Date } | null | undefined;
  if (!post || !targetDate || !post.createdAt) return -1;

  // createdAt 필드만 사용 (Firebase Timestamp 형식)
  let postDate = null;
  
  if ((post.createdAt as { _seconds?: number })._seconds) {
    postDate = new Date(((post.createdAt as { _seconds: number })._seconds) * 1000);
  } else {
    postDate = new Date(post.createdAt as string | number | Date);
  }

  if (!postDate || isNaN(postDate.getTime())) return -1;

  // `Date - Date` 는 valueOf(=getTime) 강제변환과 동일 — 수치 무변
  const diffTime = Math.abs(targetDate.getTime() - postDate.getTime());
  
  switch (range) {
    case 'hours':
      return Math.ceil(diffTime / (1000 * 60 * 60));
    case 'days':
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    case 'weeks':
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    default:
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
};

/**
 * 포스트가 특정 시간 범위 내에 있는지 확인하는 헬퍼 함수들
 */
export const isWithin24Hours = (post: unknown, now = new Date()) => indexOfDate(post, now, 'hours') <= 24;


// ----------------------------------------------------------------------

// Date normalization helpers for server filters (search params)

/**
 * Normalize various date input types (Date | string | dayjs | Firestore Timestamp) to JS Date
 * Firestore Timestamp: toDate() 또는 { seconds, _seconds, nanoseconds, _nanoseconds } 지원
 */
function toJSDate(rawInput: unknown): Date | null {
  if (!rawInput) return null;
  const input = rawInput as { toDate?: () => Date; isValid?: () => boolean; seconds?: number; _seconds?: number; nanoseconds?: number; _nanoseconds?: number };
  try {
    if (typeof input?.toDate === 'function') return input.toDate(); // Firestore Timestamp
    // 윗줄이 toDate 있는 경우를 전부 가져가 실제로는 죽은 갈래다 — TS 좁히기(never)만 캐스트로 푼다
    if (typeof input?.isValid === 'function' && typeof input?.toDate === 'function') return (input.toDate as () => Date)(); // dayjs
    const sec = input.seconds ?? input._seconds;
    const nanosec = input.nanoseconds ?? input._nanoseconds ?? 0;
    if (typeof sec === 'number') return new Date(sec * 1000 + nanosec / 1e6);
    const d = new Date(rawInput as string | number | Date);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/**
 * Format a date to YYYYMMDD string (e.g., 20250131). Returns '' if invalid.
 */
export function toYMD(input: unknown) {
  const d = toJSDate(input);
  if (!d) return '';
  return dayjs(d).format('YYYYMMDD');
}


/**
 * YYYYMMDD(숫자 또는 문자열) → Date. Returns null if invalid.
 */
function parseYmdToDate(value: unknown) {
  if (value == null || value === '') return null;
  const str = String(value).trim();
  if (!/^\d{8}$/.test(str)) return null;
  const y = Number(str.slice(0, 4));
  const m = Number(str.slice(4, 6)) - 1;
  const d = Number(str.slice(6, 8));
  const date = new Date(y, m, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * 포스트 날짜 기준으로 "N분 전", "N시간 전", "N일 전" 상대 시간 라벨 반환.
 *
 * ⚠️ `null`·`undefined` 도 받는다 — `getDateFromPost` 가 빈 값을 받아 `null` 을 돌려주므로
 * 빈 문자열이 나올 뿐이다. `Object` 로 좁히면 `.tsx` 호출부가 막힌다.
 *
 * @param {Record<string, any>|null|undefined} post 포스트 객체 (getDateFromPost로 파싱 가능한 필드 포함)
 * @returns {string} '' 또는 'N분 전' | 'N시간 전' | 'N일 전'
 */
export function getRelativeTimeLabel(post: unknown) {
  const d = getDateFromPost(post);
  if (!d) return '';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 60) return `${diffMin}분 전`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}시간 전`;
  return `${Math.floor(diffMin / 1440)}일 전`;
}

/**
 * 게시물 객체에서 발행일 Date 추출 (timestamp, date, postDate 필드 기준).
 * timestamp: ISO 문자열 또는 Unix 초/ms, date/postDate: YYYYMMDD 또는 파싱 가능한 문자열.
 * @param {Object} post - 게시물 객체
 * @returns {Date|null}
 */
export function getDateFromPost(rawPost: unknown): Date | null {
  const post = rawPost as { timestamp?: unknown; date?: unknown; postDate?: unknown } | null | undefined;
  if (!post) return null;
  if (post.timestamp != null) {
    if (typeof post.timestamp === 'string') {
      const d = new Date(post.timestamp.trim());
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const ms = Number(post.timestamp) < 1e12 ? Number(post.timestamp) * 1000 : Number(post.timestamp);
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (post.date) {
    const parsed = parseYmdToDate(post.date);
    if (parsed) return parsed;
    const d = toJSDate(post.date);
    if (d) return d;
  }
  if (post.postDate != null) {
    const parsed = parseYmdToDate(post.postDate);
    if (parsed) return parsed;
  }
  return null;
}


// ----------------------------------------------------------------------

/**
 * 오늘 기준 과거 N일 구간의 YYYYMMDD 날짜 범위 반환.
 * 엘라스틱 postDate 필터 등 검색 필터에서 그대로 사용.
 * @param {number} [days=7] - 포함할 과거 일수 (기본 1주일)
 * @returns {{ startDate: string, endDate: string }} startDate(과거), endDate(오늘) YYYYMMDD
 */
export function getYmdRangeLastDays(days = 7) {
  const end = dayjs().startOf('day');
  const start = end.subtract(Math.max(0, Number(days) || 0), 'day');
  return {
    startDate: start.format('YYYYMMDD'),
    endDate: end.format('YYYYMMDD'),
  };
}
